import React, { useState, useEffect } from 'react';
import { Calendar, Phone, Mail, MapPin, Clock, Eye, Check, X, Filter, MessageSquare, CalendarDays, HardDrive, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RequestsAdmin = () => {
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' or 'inquiries'
  const [selectedItem, setSelectedItem] = useState(null);
  const [storageStats, setStorageStats] = useState(null);

  useEffect(() => {
    fetchData();
    fetchStorageStats();
  }, []);

  const fetchStorageStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/storage-stats`);
      if (response.ok) {
        const data = await response.json();
        setStorageStats(data);
      }
    } catch (error) {
      console.error('Error fetching storage stats:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, inquiriesRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings/requests`),
        fetch(`${API_URL}/api/contact/inquiries`)
      ]);

      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data);
      }
      if (inquiriesRes.ok) {
        const data = await inquiriesRes.json();
        setInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/requests/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Status actualizat!');
        fetchData();
        setSelectedItem(null);
      }
    } catch (error) {
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi această programare?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/requests/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Programare ștearsă!');
        fetchData();
        setSelectedItem(null);
      } else {
        toast.error('Eroare la ștergere');
      }
    } catch (error) {
      toast.error('Eroare la ștergerea programării');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest mesaj?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/contact/inquiry/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Mesaj șters!');
        fetchData();
        setSelectedItem(null);
      } else {
        toast.error('Eroare la ștergere');
      }
    } catch (error) {
      toast.error('Eroare la ștergerea mesajului');
    }
  };

  const getServiceName = (service) => {
    const names = {
      'couch': 'Curățare Canapele',
      'mattress': 'Curățare Saltele',
      'both': 'Canapele + Saltele'
    };
    return names[service] || service;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'new': 'bg-purple-100 text-purple-800',
      'contacted': 'bg-blue-100 text-blue-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    const labels = {
      'pending': 'În așteptare',
      'confirmed': 'Confirmat',
      'completed': 'Finalizat',
      'cancelled': 'Anulat',
      'new': 'Nou',
      'contacted': 'Contactat',
      'closed': 'Închis'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPreferredDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ro-RO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2196f3]"></div>
      </div>
    );
  }

  const currentData = activeTab === 'bookings' ? bookings : inquiries;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0d2e5d]">Solicitări Clienți</h2>
          <p className="text-gray-600 text-sm">Vezi și gestionează toate solicitările</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          Actualizează
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setActiveTab('bookings')}
          className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'bookings'
              ? 'text-[#5cd26f] border-[#5cd26f]'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          Programări ({bookings.length})
        </button>
        <button
          onClick={() => setActiveTab('inquiries')}
          className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'inquiries'
              ? 'text-[#5cd26f] border-[#5cd26f]'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <MessageSquare className="w-4 h-4" />
          Oferte ({inquiries.length})
        </button>
      </div>

      {/* List */}
      {currentData.length === 0 ? (
        <Card className="p-8 text-center">
          <CalendarDays className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Nu există solicitări încă.</p>
          <p className="text-gray-400 text-sm mt-2">
            Solicitările vor apărea aici când clienții completează formularele.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {currentData.map((item) => (
            <Card 
              key={item.id} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                selectedItem?.id === item.id ? 'ring-2 ring-[#2196f3]' : ''
              }`}
              onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-[#0d2e5d]">{item.name}</span>
                      {getStatusBadge(item.status)}
                      {item.photos && item.photos.length > 0 && (
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                          📷 {item.photos.length} foto
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${item.phone}`} className="hover:text-[#2196f3]" onClick={e => e.stopPropagation()}>
                          {item.phone}
                        </a>
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${item.email}`} className="hover:text-[#2196f3]" onClick={e => e.stopPropagation()}>
                          {item.email}
                        </a>
                      </span>
                      <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                        {getServiceName(item.service)}
                      </span>
                    </div>
                    {activeTab === 'bookings' && item.preferred_date && (
                      <div className="mt-2 flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#5cd26f]" />
                        <span className="text-[#0d2e5d] font-medium">
                          {formatPreferredDate(item.preferred_date)} la {item.preferred_time}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-400">
                    {formatDate(item.created_at)}
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedItem?.id === item.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {item.address && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span className="text-gray-700">{item.address}</span>
                      </div>
                    )}
                    {(item.details || item.message) && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">{item.details || item.message}</p>
                      </div>
                    )}
                    {item.item_count && (
                      <div className="text-sm text-gray-600">
                        <strong>Număr piese:</strong> {item.item_count}
                      </div>
                    )}
                    
                    {/* Photos */}
                    {item.photos && item.photos.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Fotografii atașate:</p>
                        <div className="flex gap-2 flex-wrap">
                          {item.photos.map((photo, idx) => (
                            <img
                              key={idx}
                              src={photo}
                              alt={`Foto ${idx + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(photo, '_blank');
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions for Bookings */}
                    {activeTab === 'bookings' && (
                      <div className="flex gap-2 pt-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 hover:bg-green-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateBookingStatus(item.id, 'confirmed');
                          }}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Confirmă
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateBookingStatus(item.id, 'completed');
                          }}
                        >
                          Finalizat
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateBookingStatus(item.id, 'cancelled');
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Anulează
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 border-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBooking(item.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Șterge
                        </Button>
                      </div>
                    )}

                    {/* Actions for Inquiries */}
                    {activeTab === 'inquiries' && (
                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 border-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteInquiry(item.id);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Șterge mesaj
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Stats Summary */}
      <Card className="bg-gradient-to-r from-[#0d2e5d] to-[#2196f3] text-white">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold">{bookings.length}</p>
              <p className="text-sm opacity-80">Programări Total</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{bookings.filter(b => b.status === 'pending').length}</p>
              <p className="text-sm opacity-80">În Așteptare</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{inquiries.length}</p>
              <p className="text-sm opacity-80">Cereri Ofertă</p>
            </div>
            <div>
              <p className="text-3xl font-bold">{bookings.filter(b => b.status === 'completed').length}</p>
              <p className="text-sm opacity-80">Finalizate</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Storage Indicator */}
      {storageStats && (
        <Card className={`${
          storageStats.status === 'critical' ? 'bg-red-50 border-red-300' :
          storageStats.status === 'warning' ? 'bg-yellow-50 border-yellow-300' :
          'bg-green-50 border-green-300'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HardDrive className={`w-6 h-6 ${
                  storageStats.status === 'critical' ? 'text-red-500' :
                  storageStats.status === 'warning' ? 'text-yellow-500' :
                  'text-green-500'
                }`} />
                <div>
                  <p className="font-medium text-gray-700">Spațiu de stocare</p>
                  <p className="text-sm text-gray-500">
                    {storageStats.total_size_mb} MB din {storageStats.storage_limit_mb} MB utilizați
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${
                  storageStats.status === 'critical' ? 'text-red-600' :
                  storageStats.status === 'warning' ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {storageStats.usage_percent}%
                </p>
                {storageStats.status === 'critical' && (
                  <div className="flex items-center gap-1 text-red-600 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Spațiu aproape plin!</span>
                  </div>
                )}
                {storageStats.status === 'warning' && (
                  <p className="text-yellow-600 text-sm">Atenție la spațiu</p>
                )}
                {storageStats.status === 'ok' && (
                  <p className="text-green-600 text-sm">Spațiu suficient</p>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  storageStats.status === 'critical' ? 'bg-red-500' :
                  storageStats.status === 'warning' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${Math.min(storageStats.usage_percent, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RequestsAdmin;
